import { useCallback, useEffect, useRef, useState } from "react";

import {
  Dialog,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { ArrowLeft, ArrowRight, Calendar, Clock, Users } from "lucide-react";

import { CalendarEvent } from "@/api/calendarTypes";
import { useAuth } from "@/auth/useAuth";
import { NewMeeting, NewMeetingRef } from "@/components/Calendar/new-meeting";
import { Loader } from "@/components/loader";
import { cn } from "@/lib/utils";
import { getResourceCalendarEvents } from "@/services/graphServices/getResourceCalendarEvents";

dayjs.extend(isBetween);

export const MEETING_ROOMS = [
  { email: "moterom1@capguard.no", name: "Møterom 1", contactId: "CRMRES1" },
  { email: "moterom2@capguard.no", name: "Møterom 2", contactId: "CRMRES2" },
  { email: "moterom3@capguard.no", name: "Møterom 3", contactId: "CRMRES3" },
  { email: "moterom4@capguard.no", name: "Møterom 4", contactId: "CRMRES4" },
];

interface TimeSlot {
  start: Dayjs;
  end: Dayjs;
  isBooked: boolean;
  event?: CalendarEvent;
}

interface RoomEvents {
  [roomEmail: string]: CalendarEvent[];
}

export const MeetingRoomAvailability = () => {
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const meetingRef = useRef<NewMeetingRef>(null);
  const [roomEvents, setRoomEvents] = useState<RoomEvents>({});
  const [loading, setLoading] = useState(false);
  const { getToken } = useAuth();

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;

      const startOfDay = selectedDate.startOf("day");
      const endOfDay = selectedDate.endOf("day");

      // Fetch events for all meeting rooms
      const allRoomEvents: RoomEvents = {};
      await Promise.all(
        MEETING_ROOMS.map(async (room) => {
          const calendarEvents = await getResourceCalendarEvents(
            token,
            room.email,
            startOfDay,
            endOfDay
          );
          allRoomEvents[room.email] = calendarEvents;
        })
      );

      setRoomEvents(allRoomEvents);
    } catch (error) {
      console.error(
        "Error fetching room availability for meeting rooms",
        error
      );
    } finally {
      setLoading(false);
    }
  }, [getToken, selectedDate]);

  useEffect(() => {
    fetchEvents();

    // Poll every 60 seconds
    const intervalId = setInterval(() => {
      fetchEvents();
    }, 20000); // 60 seconds

    // Cleanup interval on unmount or when dependencies change
    return () => clearInterval(intervalId);
  }, [fetchEvents]);

  const handlePrevDay = () => {
    setSelectedDate((prev) => prev.subtract(1, "day"));
  };

  const handleNextDay = () => {
    setSelectedDate((prev) => prev.add(1, "day"));
  };

  const handleToday = () => {
    setSelectedDate(dayjs());
  };

  // Generate time slots for a specific room (30-minute intervals)
  const getTimeSlotsForRoom = (roomEmail: string): TimeSlot[] => {
    const events = roomEvents[roomEmail] || [];
    const timeSlots: TimeSlot[] = [];

    for (let hour = 8; hour < 17; hour++) {
      // First half hour (XX:00 - XX:30)
      for (let halfHour = 0; halfHour < 60; halfHour += 30) {
        const slotStart = selectedDate.hour(hour).minute(halfHour).second(0);
        // Slot ends 1 second before the next slot starts (e.g., 14:00:00 - 14:29:59)
        const slotEnd = slotStart.add(30, "minutes").subtract(1, "second");

        const bookedEvent = events.find((event) => {
          if (!event.start?.dateTime || !event.end?.dateTime) return false;

          const eventStart = dayjs(event.start.dateTime);
          const eventEnd = dayjs(event.end.dateTime);

          // Check if the event overlaps with this time slot
          // Event overlaps if: event starts before/at slot end AND event ends after/at slot start
          return (
            (eventStart.isBefore(slotEnd) || eventStart.isSame(slotEnd)) &&
            (eventEnd.isAfter(slotStart) || eventEnd.isSame(slotStart))
          );
        });

        timeSlots.push({
          start: slotStart,
          end: slotEnd,
          isBooked: !!bookedEvent,
          event: bookedEvent,
        });
      }
    }

    return timeSlots;
  };

  return (
    <div className="flex flex-col gap-2 p-2.5 rounded-xl bg-card border border-border shadow-sm">
      <NewMeeting ref={meetingRef} />

      {/* Header inspired by Google Calendar */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <div className="size-7 rounded-lg bg-primary/10 dark:bg-primary/15 flex items-center justify-center">
            <Calendar className="size-3.5 text-primary" strokeWidth={2.5} />
          </div>
          <span className="text-sm font-semibold text-foreground">
            Meeting Rooms
          </span>
        </div>
        {loading && <Loader height="8px" width="2px" />}
      </div>

      {/* Navigation bar - Calendly style */}
      <div className="flex items-center justify-between px-1 gap-2">
        <div className="flex items-center gap-1">
          <button
            onClick={handlePrevDay}
            className="size-7 rounded-lg hover:bg-accent flex items-center justify-center transition-colors"
            aria-label="Previous day"
          >
            <ArrowLeft className="size-4 text-foreground" strokeWidth={2} />
          </button>
          <button
            onClick={handleNextDay}
            className="size-7 rounded-lg hover:bg-accent flex items-center justify-center transition-colors"
            aria-label="Next day"
          >
            <ArrowRight className="size-4 text-foreground" strokeWidth={2} />
          </button>
        </div>

        <span className="text-xs font-semibold text-foreground tabular-nums">
          {selectedDate.format("ddd, MMM D")}
        </span>

        <button
          onClick={handleToday}
          className="px-2.5 py-1 text-[10px] font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Today
        </button>
      </div>

      {/* Calendar grid - inspired by modern scheduling apps */}
      <div className="flex flex-col rounded-lg overflow-hidden border border-border bg-background">
        {/* Room headers */}
        <div className="flex border-b border-border bg-muted/50">
          <div className="w-12 flex-shrink-0 border-r border-border"></div>
          {MEETING_ROOMS.map((room, i) => (
            <div
              key={room.email}
              className="flex-1 flex items-center justify-center py-1.5 border-r last:border-r-0 border-border"
            >
              <div className="size-5 rounded-md bg-primary/90 text-primary-foreground flex items-center justify-center text-[10px] font-bold">
                {i + 1}
              </div>
            </div>
          ))}
        </div>

        {/* Time slots */}
        {getTimeSlotsForRoom(MEETING_ROOMS[0].email).map((slot, index) => {
          const isFullHour = index % 2 === 0;
          const hour = 8 + Math.floor(index / 2);
          const timeLabel = `${hour}:00`;
          const tooltipLabel = `${hour}:${isFullHour ? "00" : "30"}`;
          const isCurrentTime = dayjs().isBetween(
            slot.start,
            slot.end,
            null,
            "[]"
          );

          return (
            <div
              key={index}
              className={cn(
                "flex h-4 border-t first:border-t-0 relative group",
                isCurrentTime
                  ? "bg-primary/5 border-primary/20"
                  : "border-border hover:bg-muted/30"
              )}
            >
              {/* Time label - only show on full hours */}
              {isFullHour ? (
                <div className="w-12 flex-shrink-0 flex items-center justify-center border-r border-border bg-muted/30">
                  <span className="text-[9px] font-medium text-muted-foreground tabular-nums">
                    {timeLabel}
                  </span>
                </div>
              ) : (
                <div className="w-12 flex-shrink-0 border-r border-border bg-muted/10"></div>
              )}

              {/* Current time indicator */}
              {isCurrentTime && (
                <div className="absolute left-12 right-0 top-1/2 -translate-y-1/2 h-0.5 bg-primary z-10">
                  <div className="absolute -left-1 top-1/2 -translate-y-1/2 size-1.5 rounded-full bg-primary border-2 border-background"></div>
                </div>
              )}

              {/* Room availability cells */}
              {MEETING_ROOMS.map((room) => {
                const timeSlots = getTimeSlotsForRoom(room.email);
                const slot = timeSlots[index];

                const tooltipTitle =
                  slot?.isBooked && slot.event ? (
                    <div className="space-y-0.5">
                      <div className="font-semibold text-[11px]">
                        {slot.event.subject}
                      </div>
                      <div className="text-[10px] opacity-90">
                        {dayjs(slot.event.start?.dateTime).format("HH:mm")} -{" "}
                        {dayjs(slot.event.end?.dateTime).format("HH:mm")}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      <div className="font-semibold text-[11px]">Available</div>
                      <div className="text-[10px] opacity-90">
                        Click to book
                      </div>
                    </div>
                  );

                const timeSlot = slot?.start;

                return (
                  <Tooltip
                    key={room.email}
                    title={tooltipTitle}
                    arrow
                    placement="top"
                    enterDelay={150}
                    leaveDelay={0}
                  >
                    <div
                      onClick={() => {
                        !slot?.isBooked &&
                          meetingRef?.current?.open(timeSlot, room.email);
                      }}
                      className={cn(
                        "flex-1 border-r last:border-r-0 border-border flex items-center justify-center relative transition-all h-full group/cell",
                        slot?.isBooked
                          ? "bg-red-500/20 dark:bg-red-500/30 cursor-not-allowed"
                          : "bg-green-500/15 dark:bg-green-500/20 cursor-pointer hover:bg-green-500/25 dark:hover:bg-green-500/30"
                      )}
                    >
                      {slot?.isBooked && (
                        <div className="size-1 rounded-full bg-red-600 dark:bg-red-500"></div>
                      )}
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};
